import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DashboardService } from '../dashboard/dashboard.service';
import { AuthService } from '../auth/auth.service';
import { SweetAlertService } from '../sweetalert/sweet-alert.service';
import { decrypt } from '../../shared/crypto-js/decrypt';
import { decryptPledgeData, encryptPledgeData } from '../../shared/crypto-js/crypto-js';

/**
 * Keeps BOT local session in sync with the server session.
 * Fixes: logout/login as another client on a different site, then reopen BOT
 * and still see the previous client's localStorage session.
 */
@Injectable({ providedIn: 'root' })
export class SessionSyncService implements OnDestroy {
  private started = false;
  private syncing = false;
  private lastSyncAt = 0;
  private readonly minIntervalMs = 2500;

  constructor(
    private dashboardService: DashboardService,
    private authService: AuthService,
    private router: Router,
    private sweetAlertService: SweetAlertService
  ) {}

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }
    this.started = true;
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('storage', this.onStorage);
    // Initial check shortly after boot (skip middleware login flow)
    setTimeout(() => this.syncSession('init'), 500);
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('storage', this.onStorage);
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /** Public so guards / components can force a check. */
  syncSession(reason: string = 'manual'): void {
    if (this.syncing) {
      return;
    }
    const now = Date.now();
    if (now - this.lastSyncAt < this.minIntervalMs) {
      return;
    }

    const path = (this.router.url || window.location.pathname || '').toLowerCase();
    if (path === '/' || path.includes('middleware') || path.includes('landing')) {
      return;
    }

    const localSession = localStorage.getItem('encryptedUserSession');
    if (!localSession) {
      return;
    }

    this.syncing = true;
    this.lastSyncAt = now;

    this.dashboardService.getClientData().subscribe({
      next: (data) => {
        this.syncing = false;

        if (!data || data.emsg === 'Session Expired' || !data.code) {
          this.forceLogout('Session expired. Please login again.');
          return;
        }

        try {
          const remote = JSON.parse(decrypt(data.code));
          const local = JSON.parse(decryptPledgeData(localSession));
          const remoteId = String(remote?.accountId ?? '').trim();
          const localId = String(local?.accountId ?? '').trim();

          if (!remoteId) {
            this.forceLogout('Session invalid. Please login again.');
            return;
          }

          // Different client logged in on shared SSO session — adopt new session
          if (localId && remoteId !== localId) {
            this.replaceLocalSession(remote);
            this.sweetAlertService.sweetAlert(
              'success',
              'Account switched. Loading the new client session.'
            );
            setTimeout(() => window.location.reload(), 400);
            return;
          }

          // Same client — refresh tokens if server returned newer ones
          if (
            remote.token &&
            (remote.token !== local.token ||
              remote.login_token !== local.login_token)
          ) {
            this.replaceLocalSession(remote);
          }
        } catch {
          this.forceLogout('Session invalid. Please login again.');
        }
      },
      error: () => {
        this.syncing = false;
        // Network errors: do not wipe local session
      },
    });
  }

  private replaceLocalSession(userData: any): void {
    const accountId = userData?.accountId;
    const login_token = userData?.login_token;
    const exchEnabled = userData?.exchEnabled;
    const token = userData?.token;
    const payload = { accountId, login_token, exchEnabled, token };
    localStorage.setItem(
      'encryptedUserSession',
      encryptPledgeData(JSON.stringify(payload))
    );
    // Drop cached profile so UI reloads for the new client
    localStorage.removeItem('userData');
    localStorage.removeItem('userDataTime');
  }

  private forceLogout(message: string): void {
    this.authService.isAuthenticatedSubject.next(false);
    this.authService.clearClientStorage();
    this.sweetAlertService.sweetAlert('error', message);
    this.router.navigateByUrl('/');
  }

  private readonly onVisibility = () => {
    if (document.visibilityState === 'visible') {
      this.syncSession('visibility');
    }
  };

  private readonly onFocus = () => {
    this.syncSession('focus');
  };

  private readonly onStorage = (event: StorageEvent) => {
    // Another tab cleared/changed the session
    if (event.key === 'encryptedUserSession') {
      if (!event.newValue) {
        this.authService.isAuthenticatedSubject.next(false);
        this.router.navigateByUrl('/');
        return;
      }
      // Session replaced in another tab — reload to pick up new client
      if (event.oldValue && event.newValue && event.oldValue !== event.newValue) {
        window.location.reload();
      }
    }
  };
}
